import { useCallback, useEffect, useState } from 'react'

import { generateEquationForClickVariable, ClickablePartsModel, checkAnswer } from './ClickVariable.utils'

interface UseClickVariableReturnType {
  clickableParts?: ClickablePartsModel[]
  formValid: boolean
  handleCheckAnswer: () => void
  onInputChange: (val?: number) => void
  goToNextQuestion: () => void
}

interface UseClickVariableProps {
  difficultyLevel: number
  onNextQuestionClick: () => void
  onAnswerStateChange: (value: boolean | null) => void
}

function UseClickVariable({
  onAnswerStateChange,
  onNextQuestionClick,
  difficultyLevel,
}: UseClickVariableProps): UseClickVariableReturnType {
  const [taskHistory, setTaskHistory] = useState<string[]>([])
  const [clickableParts, setClickableParts] = useState<ClickablePartsModel[]>([])
  const [equationString, setEquationString] = useState('')

  useEffect(() => {
    const [_clickableParts, _equationString] = generateEquationForClickVariable(difficultyLevel, taskHistory)
    setClickableParts(_clickableParts)
    setEquationString(_equationString)
  }, [taskHistory, difficultyLevel])

  const handleCheckAnswer = useCallback((): void => {
    const isRight = checkAnswer(clickableParts)
    onAnswerStateChange(isRight)
    if (!isRight) {
      setTimeout(() => {
        onAnswerStateChange(null)
      }, 1500)
    }
  }, [onAnswerStateChange, clickableParts])

  const onInputChange = useCallback(
    (index?: number) => {
      if (index === undefined) {
        setClickableParts((prevState) => {
          const _clickableParts: ClickablePartsModel[] = JSON.parse(JSON.stringify(prevState))
          _clickableParts.forEach((clickablePart) => {
            clickablePart.isClicked = false
          })
          return _clickableParts
        })
      } else {
        setClickableParts((prevState) => {
          const _clickableParts = JSON.parse(JSON.stringify(prevState))
          _clickableParts[index].isClicked = !_clickableParts[index].isClicked
          return _clickableParts
        })
      }

      onAnswerStateChange(null)
    },
    [onAnswerStateChange],
  )

  const goToNextQuestion = useCallback(() => {
    setTaskHistory((prevState) => [equationString, ...prevState].slice(0, 3))
    onNextQuestionClick()
  }, [onNextQuestionClick, equationString])

  return {
    clickableParts,
    formValid: true,
    handleCheckAnswer,
    onInputChange,
    goToNextQuestion,
  }
}

export default UseClickVariable
